import type {
  RichTextData,
  RichTextHighlight,
  RichTextRkpi,
} from "@library-elements/text";
import type { Node as PmNode } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { DEFAULT_LINE_HEIGHT } from "../../constants";
import { enclosingList, firstSegmentMarks, selectedRkpi } from "./commands";
import { blockImpliedStyle } from "./normalize";
import { textSchema, type BlockAttrs, type ListKind } from "./schema";
import type { TextStyleId } from "./typography";

const { nodes, marks } = textSchema;

/** Textblock (and enclosing list item) that holds the first selected segment. */
const firstBlock = (
  state: EditorState,
): { block: PmNode; listItem: PmNode | null } => {
  const { $from } = state.selection;
  let block: PmNode = $from.parent;
  let listItem: PmNode | null = null;
  if (!block.isTextblock) {
    // Node selection / gap: fall back to the first textblock in range.
    state.doc.nodesBetween(state.selection.from, state.selection.to, (node) => {
      if (block.isTextblock) return false;
      if (node.isTextblock) block = node;
      return true;
    });
  }
  for (let d = $from.depth - 1; d >= 0; d--) {
    if ($from.node(d).type === nodes.list_item) {
      listItem = $from.node(d);
      break;
    }
  }
  return { block, listItem };
};

/**
 * Computed style of the first selected segment. At a mark boundary
 * `domAtPos(pos)` returns the block, so a styled span read as the block's size
 * (QA C4); the side picks the segment firstSegmentMarks reads — the text
 * before a caret, the text after a range start.
 */
const computedAt = (
  view: EditorView,
  pos: number,
  side: -1 | 1,
): CSSStyleDeclaration | null => {
  try {
    const { node } = view.domAtPos(pos, side);
    const element = node instanceof HTMLElement ? node : node.parentElement;
    return element ? window.getComputedStyle(element) : null;
  } catch {
    return null;
  }
};

const listTypeOf = (
  state: EditorState,
): { listType: string; listIcon: string | null } => {
  const list = enclosingList(state);
  if (!list) return { listType: "none", listIcon: null };
  if (list.node.type === nodes.ordered_list)
    return { listType: "number", listIcon: null };
  const kind = list.node.attrs.kind as ListKind;
  return {
    listType: kind === "bullet" ? "bullet" : kind,
    listIcon: (list.node.attrs.iconName as string | null) ?? null,
  };
};

/**
 * The toolbar's view of the editor, in the exact shape the FroalaToolbar
 * consumes (RichTextData). Spec §10: everything reflects the first text
 * segment of the selection.
 */
export const deriveRichTextData = (view: EditorView): RichTextData => {
  const { state } = view;
  const { from, to, empty, $from } = state.selection;
  const segmentMarks = firstSegmentMarks(state);
  const { block, listItem } = firstBlock(state);
  const computed = computedAt(
    view,
    from,
    empty && $from.parentOffset > 0 ? -1 : 1,
  );
  // Block-level settings live on the list item when inside a list.
  const blockAttrs = (listItem ?? block).attrs as BlockAttrs;

  const styleMark = marks.text_style.isInSet(segmentMarks);
  const textStyle: TextStyleId = styleMark
    ? (styleMark.attrs.style as TextStyleId)
    : blockImpliedStyle(block, listItem);

  const colorMark = marks.color.isInSet(segmentMarks);
  const highlightMark = marks.highlight.isInSet(segmentMarks);
  const linkMark = marks.link.isInSet(segmentMarks);
  const classificationMark = marks.ai_classification.isInSet(segmentMarks);

  const textAlign =
    blockAttrs.align ??
    (computed?.textAlign === "start" ? "left" : computed?.textAlign) ??
    "left";

  const highlight: RichTextHighlight | null = highlightMark
    ? {
        bgColor: highlightMark.attrs.bgColor as string,
        textColor: highlightMark.attrs.textColor as string,
        animation: highlightMark.attrs
          .animation as RichTextHighlight["animation"],
        static: !!highlightMark.attrs.static,
      }
    : null;

  const link: RichTextData["link"] = linkMark
    ? {
        type: linkMark.attrs.anchor ? "anchor" : "hyperlink",
        event: (linkMark.attrs.event as string | null) ?? "",
        url: linkMark.attrs.anchor
          ? `#${linkMark.attrs.anchor as string}`
          : (linkMark.attrs.href as string),
        status: "update",
        openInPopup: !!linkMark.attrs.openInPopup,
      }
    : { type: "hyperlink", event: "", url: "", status: "create" };

  const rkpi = selectedRkpi(state);
  const rkpiData: RichTextRkpi | null = rkpi ? { ...rkpi, delay: 0 } : null;

  return {
    fontSize:
      (marks.font_size.isInSet(segmentMarks)?.attrs.px as number | undefined) ??
      // Scaled layouts compute 41.9996px; the size box and its Default row
      // (FontSizeController rounds the design-system size) both want 42.
      Math.round(Number.parseFloat(computed?.fontSize ?? "0") || 0),
    textAlign,
    textColor:
      (colorMark?.attrs.color as string | undefined) ?? computed?.color ?? "",
    isBold: !!marks.strong.isInSet(segmentMarks),
    isItalic: !!marks.em.isInSet(segmentMarks),
    isUnderline: !!marks.underline.isInSet(segmentMarks),
    isStrikethrough: !!marks.strike.isInSet(segmentMarks),
    textDirection: blockAttrs.direction === "rtl" ? "rtl" : "ltr",
    highlight,
    textStyle,
    ...listTypeOf(state),
    lineHeight: blockAttrs.lineHeight ?? DEFAULT_LINE_HEIGHT,
    link,
    subscript: !!marks.subscript.isInSet(segmentMarks),
    superscript: !!marks.superscript.isInSet(segmentMarks),
    selectedRkpi: rkpiData,
    selectionContent: state.doc.textBetween(from, to, " "),
    textAnimation: blockAttrs.animation
      ? {
          speed: blockAttrs.animation.speed,
          baseOpacity: blockAttrs.animation.baseOpacity,
        }
      : null,
    aiClassification: (classificationMark?.attrs.value as string) ?? null,
  };
};
